import type {
  VibeGuardConfig,
  Level,
  RuleDefinition,
  CheckResult,
  RuleSeverity,
  RuleConfig,
} from '../types/index.js';
import { rules, getRuleById } from '../rules/index.js';
import { getPreset } from '../presets/index.js';

export interface ResolvedRule {
  definition: RuleDefinition;
  severity: RuleSeverity;
  enabled: boolean;
  blocking: boolean;
  autofix: boolean;
}

export function resolveRules(config: VibeGuardConfig): ResolvedRule[] {
  const level: Level = config.level ?? 'standard';
  let activeRuleIds: string[] = [];

  if (config.preset) {
    const preset = getPreset(config.preset);
    activeRuleIds = preset.rules[level] ?? [];
  } else {
    activeRuleIds = rules.filter((r) => r.levels[level]).map((r) => r.id);
  }

  const resolvedRules: ResolvedRule[] = [];

  for (const ruleId of activeRuleIds) {
    const definition = getRuleById(ruleId);
    if (!definition) continue;

    const ruleConfig = config.rules?.[ruleId];
    let enabled = true;
    let severity = definition.severity;
    let blocking = definition.blocking;
    let autofix = definition.autofix;

    if (typeof ruleConfig === 'boolean') {
      enabled = ruleConfig;
    } else if (ruleConfig) {
      enabled = ruleConfig.enabled ?? true;
      severity = ruleConfig.severity ?? severity;
      blocking = ruleConfig.blocking ?? blocking;
      autofix = ruleConfig.autofix ?? autofix;
    }

    if (enabled) {
      resolvedRules.push({
        definition,
        severity,
        enabled,
        blocking,
        autofix,
      });
    }
  }

  if (config.rules) {
    for (const [ruleId, ruleConfig] of Object.entries(config.rules)) {
      if (activeRuleIds.includes(ruleId)) continue;

      const definition = getRuleById(ruleId);
      if (!definition) continue;

      let enabled = true;
      if (typeof ruleConfig === 'boolean') {
        enabled = ruleConfig;
      } else {
        enabled = ruleConfig.enabled ?? true;
      }

      if (enabled) {
        resolvedRules.push({
          definition,
          severity:
            typeof ruleConfig === 'object' ? ruleConfig.severity ?? definition.severity : definition.severity,
          enabled,
          blocking:
            typeof ruleConfig === 'object' ? ruleConfig.blocking ?? definition.blocking : definition.blocking,
          autofix:
            typeof ruleConfig === 'object' ? ruleConfig.autofix ?? definition.autofix : definition.autofix,
        });
      }
    }
  }

  return resolvedRules;
}

export function groupRulesByTool(resolvedRules: ResolvedRule[]): Map<string, ResolvedRule[]> {
  const grouped = new Map<string, ResolvedRule[]>();

  for (const rule of resolvedRules) {
    const tool = rule.definition.tool;
    const existing = grouped.get(tool) ?? [];
    existing.push(rule);
    grouped.set(tool, existing);
  }

  return grouped;
}

export function calculateScore(results: CheckResult[]): number {
  if (results.length === 0) return 100;

  const weights = { error: 10, warn: 3, info: 1 };
  let totalPenalty = 0;

  for (const result of results) {
    totalPenalty += weights[result.severity] ?? 0;
  }

  const score = Math.max(0, 100 - totalPenalty);
  return Math.round(score);
}

export function shouldFail(results: CheckResult[], failOn: RuleSeverity): boolean {
  const severityOrder: RuleSeverity[] = ['info', 'warn', 'error'];
  const failIndex = severityOrder.indexOf(failOn);

  return results.some((r) => severityOrder.indexOf(r.severity) >= failIndex);
}
